import type { FormComponent, FormDSL } from '@commenda/types';
import { COMMENDA_OS_DATA_SOURCE } from '../dsl-constants';
import type { ComponentType } from '../types';

export interface ContextRequirements {
  needsCompany: boolean;
  needsCorporation: boolean;
  needsDirector: boolean;
}

const CORPORATION_ENTITIES = new Set([
  'corporation',
  'governmentIdentifier',
  'bankAccount',
  'document',
  'registeredAgent',
  'location',
  'keyPersonRelationship',
]);

const DIRECTOR_ENTITIES = new Set([
  'keyPerson',
  'keyPersonRelationship',
  'directorDocument',
]);

const CORPORATION_COMPONENTS: Set<string> = new Set<ComponentType>([
  'GovernmentIdentifier',
  'BankAccount',
  'Document',
  'RegisteredAgent',
  'Location',
  'BusinessEntitySelectOrCreate',
]);

const DIRECTOR_COMPONENTS: Set<string> = new Set<ComponentType>([
  'DirectorDocument',
  'KeyPersons',
]);

function collectComponents(dsl: FormDSL): FormComponent[] {
  const components: FormComponent[] = [];
  for (const step of dsl.steps) {
    for (const mod of step.modules) {
      for (const comp of mod.components) {
        components.push(comp);
        if (comp.template) {
          for (const nested of comp.template) {
            components.push(nested);
          }
        }
      }
    }
  }
  return components;
}

/**
 * Scan a FormDSL for CommendaOS fields and determine which context IDs
 * the preview modal needs to collect from the user.
 */
export function detectCommendaOSContextNeeds(
  dsl: FormDSL
): ContextRequirements {
  let needsCorporation = false;
  let needsDirector = false;
  let hasCOSField = false;

  const components = collectComponents(dsl);

  for (const comp of components) {
    const ds = comp.dataSource;
    const isCOS =
      ds?.type === COMMENDA_OS_DATA_SOURCE && !!ds.commendaOS?.entity;

    if (isCOS) {
      hasCOSField = true;
      const entity = ds!.commendaOS!.entity;
      if (CORPORATION_ENTITIES.has(entity)) needsCorporation = true;
      if (DIRECTOR_ENTITIES.has(entity)) needsDirector = true;
    }

    if (CORPORATION_COMPONENTS.has(comp.component)) {
      hasCOSField = true;
      needsCorporation = true;
    }

    if (DIRECTOR_COMPONENTS.has(comp.component)) {
      hasCOSField = true;
      needsDirector = true;
    }
  }

  // Also honour form-level contextRequirements
  const required = dsl.form?.contextRequirements?.required ?? [];
  if (required.includes('companyId')) hasCOSField = true;
  if (required.includes('corporationId')) {
    hasCOSField = true;
    needsCorporation = true;
  }
  if (required.includes('directorId')) {
    hasCOSField = true;
    needsDirector = true;
  }

  return {
    needsCompany: hasCOSField,
    needsCorporation,
    needsDirector,
  };
}
